import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, RefreshCcw, Sparkles, Send, X, Download, Share2, MessageCircle, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";

// -----------------------------------------------------------------------------
// Steps: intro -> camera -> preview -> submitted
// -----------------------------------------------------------------------------
export default function Landing() {
  const [step, setStep] = useState("intro");
  const [activeFrame, setActiveFrame] = useState(null);
  const [activeStrip, setActiveStrip] = useState(null);
  const [loadingFrame, setLoadingFrame] = useState(true);
  const [photo, setPhoto] = useState(null); // dataURL jpeg
  const [submission, setSubmission] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [frameRes, stripRes] = await Promise.all([
          api.get("/frames/active").catch(() => ({ data: null })),
          api.get("/strips/active").catch(() => ({ data: null })),
        ]);
        setActiveFrame(frameRes.data || null);
        setActiveStrip(stripRes.data || null);
      } finally {
        setLoadingFrame(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-[100dvh] w-full bg-background" data-testid="landing-page">
      <AnimatePresence mode="wait">
        {step === "intro" && (
          <Intro
            key="intro"
            activeFrame={activeFrame}
            loading={loadingFrame}
            onStart={() => setStep("camera")}
            data-testid="intro-screen"
          />
        )}
        {step === "camera" && (
          <CameraScreen
            key="camera"
            activeFrame={activeFrame}
            onBack={() => setStep("intro")}
            onCapture={(payload) => {
              setPhoto(payload); // { raw, preview }
              setStep("preview");
            }}
          />
        )}
        {step === "preview" && (
          <PreviewScreen
            key="preview"
            photo={photo}
            activeFrame={activeFrame}
            activeStrip={activeStrip}
            frameId={activeFrame?.id}
            onRetake={() => {
              setPhoto(null);
              setStep("camera");
            }}
            onSubmitted={(sub) => {
              setSubmission(sub);
              setStep("submitted");
            }}
          />
        )}
        {step === "submitted" && submission && (
          <SubmittedScreen
            key="submitted"
            submission={submission}
            onAnother={() => {
              setSubmission(null);
              setPhoto(null);
              setStep("camera");
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// -----------------------------------------------------------------------------
// INTRO
// -----------------------------------------------------------------------------
function Intro({ activeFrame, loading, onStart }) {
  return (
    <motion.div
      className="relative min-h-[100dvh] flex flex-col items-center justify-center px-6 py-12 overflow-hidden dot-grid"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
    >
      {/* Floating shapes */}
      <motion.div
        className="absolute -top-10 -left-10 h-48 w-48 rounded-full bg-[#CCFF00] blur-3xl opacity-40"
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 6, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-0 -right-10 h-60 w-60 rounded-full bg-[#FF4D4D] blur-3xl opacity-30"
        animate={{ y: [0, -25, 0] }}
        transition={{ duration: 7, repeat: Infinity }}
      />

      <div className="relative z-10 max-w-md w-full text-center my-auto">
        <motion.div
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-border mb-4"
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <Sparkles className="h-4 w-4 text-[#FF4D4D]" />
          <span className="text-sm font-semibold tracking-wide">LIVE WISH CAM</span>
        </motion.div>

        <motion.h1
          className="text-4xl md:text-5xl font-black tracking-tight leading-[0.95] mb-4"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Snap a selfie.<br />
          <span className="relative inline-block">
            <span className="relative z-10 px-2">Send the</span>
            <span className="absolute inset-0 bg-[#CCFF00] -rotate-1 rounded-lg" />
          </span>{" "}
          <span className="text-[#FF4D4D]">love.</span>
        </motion.h1>

        <motion.p
          className="text-muted-foreground text-sm md:text-base mb-6 max-w-sm mx-auto"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Take a photo with our custom frame, write your wish, and share it with the world.
        </motion.p>

        {/* Active frame preview — shows celebrity position (right side) */}
        <motion.div
          className="mb-6 relative mx-auto w-44 h-44 md:w-52 md:h-52 rounded-3xl bg-white border border-border overflow-hidden grain"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4, type: "spring" }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#FFE5E5] to-[#F5FFD0]" />
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : activeFrame ? (
            <>
              {/* 'You' placeholder filling whole square */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-[55%] w-[55%] rounded-full bg-white/50 border border-white/70" />
              </div>
              {/* Celeb bottom-right, in front */}
              <img
                src={fileUrl(activeFrame.storage_path)}
                alt="Active celebrity"
                className="absolute bottom-0 right-0 max-h-[95%] max-w-[55%] w-auto h-auto object-contain drop-shadow-lg"
                data-testid="active-frame-preview"
              />
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center">
              <Camera className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-xs text-muted-foreground">No frame set yet. Still looks great.</p>
            </div>
          )}
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <Button
            onClick={onStart}
            data-testid="start-camera-btn"
            className="h-14 px-10 text-base font-bold rounded-full bg-[#FF4D4D] hover:bg-[#FF7373] text-white shadow-[0_8px_32px_rgba(255,77,77,0.35)] transition-all active:scale-95"
          >
            <Camera className="h-5 w-5 mr-2" />
            Tap to start
          </Button>
        </motion.div>

        <p className="text-xs text-muted-foreground mt-6">
          We'll ask for camera access next.
        </p>
      </div>
    </motion.div>
  );
}

// -----------------------------------------------------------------------------
// CAMERA
// -----------------------------------------------------------------------------
function CameraScreen({ activeFrame, onBack, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [flash, setFlash] = useState(false);
  const [error, setError] = useState(null);
  const [facing, setFacing] = useState("user");
  const [ready, setReady] = useState(false);
  const [starting, setStarting] = useState(true);

  const frameSrc = activeFrame ? fileUrl(activeFrame.storage_path) : null;

  const attachStream = useCallback(async (stream) => {
    streamRef.current = stream;
    if (!videoRef.current) return;
    videoRef.current.srcObject = stream;
    try {
      await videoRef.current.play();
    } catch (e) {
      console.warn("video.play() failed, will retry on user tap", e);
    }
    setReady(true);
  }, []);

  const startCamera = useCallback(async (mode) => {
    setError(null);
    setReady(false);
    setStarting(true);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("NOT_SUPPORTED");
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: mode } },
          audio: false,
        });
      } catch (err) {
        // Fallback: some devices (desktops) don't have front/back distinction
        console.warn("Primary getUserMedia failed, trying generic", err);
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      }
      await attachStream(stream);
    } catch (e) {
      const name = e?.name || "";
      const msg = e?.message || "";
      console.error("Camera error:", name, msg, e);
      let friendly;
      if (msg === "NOT_SUPPORTED") {
        friendly = "Your browser does not support camera access. Try Chrome or Safari on a mobile device.";
      } else if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        friendly = "Camera permission denied. Please enable camera access for this site in your browser settings and retry.";
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        friendly = "No camera found on this device.";
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        friendly = "Camera is already in use by another app. Close other camera apps and retry.";
      } else if (name === "OverconstrainedError") {
        friendly = "Your camera doesn't match requested settings. Retry with default settings.";
      } else if (name === "SecurityError" || msg.toLowerCase().includes("permission")) {
        friendly = "Camera blocked by browser security policy. If you are viewing inside an embedded frame, open the site directly in a new tab.";
      } else {
        friendly = msg || "Unable to start camera. Please retry.";
      }
      setError(friendly);
    } finally {
      setStarting(false);
    }
  }, [attachStream]);

  useEffect(() => {
    startCamera(facing);
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, [facing, startCamera]);

  // (Frame/strip are overlaid server-side in /api/submissions — no client canvas needed.)

  const capture = async () => {
    if (!videoRef.current || !ready) return;
    setFlash(true);
    setTimeout(() => setFlash(false), 250);

    const video = videoRef.current;
    const vw = video.videoWidth || 720;
    const vh = video.videoHeight || 720;

    // Square output — user video center-cropped. RAW selfie only.
    // Server overlays celeb + strip + wish from its own storage for the
    // final saved image. Preview screen shows celeb via pure CSS overlay.
    const size = Math.min(vw, vh);
    const sx = (vw - size) / 2;
    const sy = (vh - size) / 2;

    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");

    ctx.save();
    if (facing === "user") {
      ctx.translate(size, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, sx, sy, size, size, 0, 0, size, size);
    ctx.restore();

    const rawDataUrl = canvas.toDataURL("image/jpeg", 0.92);
    onCapture({ raw: rawDataUrl, preview: rawDataUrl });
  };

  return (
    <motion.div
      className="relative h-[100dvh] w-full bg-black overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="camera-screen"
    >
      {/* Square preview stage: matches captured composition 1:1 */}
      <div className="absolute inset-0 flex items-center justify-center px-3 pt-16 pb-28">
        <div className="relative aspect-square w-full max-w-[560px] max-h-full rounded-3xl overflow-hidden shadow-2xl bg-black">
          {/* Layer 1 (behind): USER video fills whole square (top, bottom, left, right) */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={`absolute inset-0 w-full h-full object-cover z-0 ${facing === "user" ? "scale-x-[-1]" : ""}`}
            data-testid="camera-video"
          />

          {/* Layer 2 (front): CELEB — bottom-right, flush. Display-only. Server composes the saved image. */}
          {frameSrc && (
            <img
              src={frameSrc}
              alt="celebrity"
              className="absolute bottom-0 right-0 max-h-[90%] max-w-[50%] w-auto h-auto object-contain z-10 drop-shadow-[0_6px_20px_rgba(0,0,0,0.35)] pointer-events-none"
              data-testid="frame-overlay"
            />
          )}
        </div>
      </div>

      {/* Top bar (glassmorphism) */}
      <div className="absolute top-0 inset-x-0 p-4 z-20 flex items-center justify-between">
        <button
          onClick={onBack}
          data-testid="camera-back-btn"
          className="h-11 w-11 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-xl border border-white/30 text-white hover:bg-white/30 transition"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="px-4 py-2 rounded-full bg-white/20 backdrop-blur-xl border border-white/30 text-white text-sm font-semibold">
          Smile!
        </div>
        <button
          onClick={() => setFacing((f) => (f === "user" ? "environment" : "user"))}
          data-testid="camera-flip-btn"
          className="h-11 w-11 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-xl border border-white/30 text-white hover:bg-white/30 transition"
        >
          <RefreshCcw className="h-5 w-5" />
        </button>
      </div>

      {/* Bottom shutter */}
      <div className="absolute bottom-0 inset-x-0 z-20 p-8 flex items-center justify-center">
        <button
          onClick={capture}
          disabled={!ready}
          aria-label="Capture"
          data-testid="capture-btn"
          className="h-20 w-20 rounded-full bg-white flex items-center justify-center border-4 border-[#FF4D4D] shutter-glow transition active:scale-95 disabled:opacity-50"
        >
          <span className="h-14 w-14 rounded-full bg-[#FF4D4D] group-active:scale-90 transition" />
        </button>
      </div>

      {/* Flash */}
      <AnimatePresence>
        {flash && (
          <motion.div
            className="absolute inset-0 bg-white z-30 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
        )}
      </AnimatePresence>

      {/* Starting overlay */}
      {starting && !error && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black/70 text-white gap-3" data-testid="camera-starting">
          <Loader2 className="h-8 w-8 animate-spin" />
          <div className="text-sm font-semibold">Starting camera…</div>
          <div className="text-xs text-white/70 px-8 text-center max-w-sm">
            If prompted, please allow camera access.
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/80 p-6" data-testid="camera-error">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center">
            <h3 className="font-bold text-lg mb-2">Camera unavailable</h3>
            <p className="text-sm text-muted-foreground mb-5">{error}</p>
            <div className="flex gap-2">
              <Button
                onClick={() => startCamera(facing)}
                className="flex-1 h-11 rounded-full bg-[#FF4D4D] hover:bg-[#FF7373] text-white font-bold"
                data-testid="camera-retry-btn"
              >
                <RefreshCcw className="h-4 w-4 mr-2" />
                Retry
              </Button>
              <Button onClick={onBack} variant="outline" className="h-11 rounded-full font-bold" data-testid="camera-error-back-btn">
                Back
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-4">
              Tip: On iPhone use Safari. On Android use Chrome. Make sure the site is not inside an embed/iframe.
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
}

// -----------------------------------------------------------------------------
// PREVIEW + WISH FORM
// -----------------------------------------------------------------------------
function PreviewScreen({ photo, frameId, activeStrip, activeFrame, onRetake, onSubmitted }) {
  // photo is { raw: dataURL, preview: dataURL }
  const rawPhoto = photo?.raw || photo; // backward-compat
  const [openForm, setOpenForm] = useState(false);
  const [wish, setWish] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const frameUrl = activeFrame ? fileUrl(activeFrame.storage_path) : null;
  const stripUrl = activeStrip ? fileUrl(activeStrip.storage_path) : null;

  const handleSubmit = async () => {
    if (!wish.trim()) return toast.error("Please write your wish");
    if (!name.trim()) return toast.error("Please enter your name");
    if (!phone.trim() || phone.trim().length < 4) return toast.error("Please enter a valid phone number");

    setSubmitting(true);
    try {
      // Send ONLY the raw selfie. Server overlays celeb + strip + wish using
      // its own copies from storage — bulletproof, no CORS or canvas issues.
      const { data } = await api.post("/submissions", {
        name: name.trim(),
        phone: phone.trim(),
        wish: wish.trim(),
        image_base64: rawPhoto,
        frame_id: frameId || null,
      });
      toast.success("Wish sent!");
      onSubmitted(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      className="min-h-[100dvh] relative flex flex-col bg-background"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="preview-screen"
    >
      <div className="flex items-center justify-between p-4">
        <button
          onClick={onRetake}
          data-testid="retake-btn"
          className="h-11 px-4 rounded-full flex items-center gap-2 bg-white border border-border hover:bg-muted transition font-semibold text-sm"
        >
          <RefreshCcw className="h-4 w-4" />
          Retake
        </button>
        <div className="text-sm font-semibold text-muted-foreground">Preview</div>
        <div className="w-[88px]" />
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        {/* Pure HTML/CSS overlay preview — matches server composition exactly,
            but uses no canvas, so it can't fail due to browser CORS. */}
        <div className="relative w-full max-w-md rounded-3xl overflow-hidden bg-white shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)]">
          {/* Square: user selfie + celeb on top-right */}
          <div className="relative aspect-square w-full bg-black overflow-hidden">
            <img src={rawPhoto} alt="Your selfie" className="absolute inset-0 w-full h-full object-cover" data-testid="photo-preview" />
            {frameUrl && (
              <img
                src={frameUrl}
                alt="celebrity"
                className="absolute bottom-0 right-0 max-h-[90%] max-w-[50%] w-auto h-auto object-contain drop-shadow-[0_6px_20px_rgba(0,0,0,0.35)] pointer-events-none"
              />
            )}
          </div>
          {/* Bottom strip */}
          {stripUrl && (
            <img src={stripUrl} alt="Wish banner" className="w-full h-auto block" />
          )}
          {/* Wish text preview (optional) */}
          {wish.trim() && (
            <div className="bg-white border-t-[6px] border-[#FF4D4D] px-8 py-5">
              <p className="text-[#0C0C0C] text-[15px] leading-snug font-semibold line-clamp-3">{wish}</p>
            </div>
          )}
        </div>
      </div>

      <div className="p-6 pt-0">
        <Button
          onClick={() => setOpenForm(true)}
          data-testid="write-wish-btn"
          className="w-full h-14 rounded-full bg-[#0C0C0C] hover:bg-black text-white font-bold text-base"
        >
          Write your wish
          <Sparkles className="h-5 w-5 ml-2" />
        </Button>
      </div>

      <Drawer open={openForm} onOpenChange={setOpenForm}>
        <DrawerContent className="bg-white">
          <DrawerHeader className="text-left">
            <DrawerTitle className="text-2xl font-black" style={{ fontFamily: "Outfit, sans-serif" }}>
              Send your wish
            </DrawerTitle>
            <DrawerDescription>
              Your wish appears on the dashboard. Name and phone stay private with the admin.
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-6 space-y-4">
            <div>
              <label className="text-sm font-semibold mb-2 block">Your wish ✨</label>
              <Textarea
                value={wish}
                onChange={(e) => setWish(e.target.value)}
                placeholder="Happy birthday! May your year be full of…"
                rows={4}
                className="resize-none rounded-2xl border-border focus-visible:ring-[#FF4D4D]"
                maxLength={500}
                data-testid="wish-textarea"
              />
              <div className="text-xs text-muted-foreground mt-1 text-right">{wish.length}/500</div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="text-sm font-semibold mb-2 block">Name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="h-12 rounded-2xl border-border focus-visible:ring-[#FF4D4D]"
                  data-testid="name-input"
                />
              </div>
              <div>
                <label className="text-sm font-semibold mb-2 block">Phone</label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 234 567 890"
                  type="tel"
                  className="h-12 rounded-2xl border-border focus-visible:ring-[#FF4D4D]"
                  data-testid="phone-input"
                />
              </div>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="submit-wish-btn"
              className="w-full h-14 rounded-full bg-[#FF4D4D] hover:bg-[#FF7373] text-white font-bold text-base shadow-[0_8px_32px_rgba(255,77,77,0.35)]"
            >
              {submitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" /> Submit wish
                </>
              )}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </motion.div>
  );
}

// Helper: load an image into an HTMLImageElement (with CORS for canvas export)

// -----------------------------------------------------------------------------
// SUBMITTED + SHARE
// -----------------------------------------------------------------------------
function SubmittedScreen({ submission, onAnother }) {
  const imageUrl = fileUrl(submission.image_path);
  const shareText = `${submission.wish} — ${submission.name}`;
  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(imageUrl);
  const [sharing, setSharing] = useState(false);

  // Fetch the actual image file once and keep it cached for reuse
  const getImageFile = async () => {
    const res = await fetch(imageUrl);
    const blob = await res.blob();
    return new File([blob], `wish-${submission.id}.jpg`, { type: blob.type || "image/jpeg" });
  };

  const canShareFile = (file) =>
    typeof navigator !== "undefined" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [file] });

  // Shares the actual image file via Web Share API (native sheet picks WhatsApp/Instagram/etc.)
  // Fallbacks: if no file share, falls back to given URL intent.
  const shareImage = async (fallbackUrl) => {
    if (sharing) return;
    setSharing(true);
    try {
      const file = await getImageFile();
      if (canShareFile(file)) {
        await navigator.share({ files: [file], text: shareText });
        return;
      }
      if (navigator.share) {
        try {
          await navigator.share({ text: shareText, url: imageUrl });
          return;
        } catch (e) {
          if (e?.name === "AbortError") return;
        }
      }
      // Final fallback: download then open the platform URL so user can attach manually
      await downloadImage(file);
      if (fallbackUrl) window.open(fallbackUrl, "_blank", "noopener");
      toast.info("Image saved to your device — attach it when posting.");
    } catch (e) {
      if (e?.name === "AbortError") return;
      toast.error("Could not share. Tap Download instead.");
    } finally {
      setSharing(false);
    }
  };

  const downloadImage = async (existingFile) => {
    try {
      const file = existingFile || (await getImageFile());
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(imageUrl, "_blank");
    }
  };

  return (
    <motion.div
      className="min-h-[100dvh] relative dot-grid"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="submitted-screen"
    >
      <div className="max-w-md mx-auto px-6 py-10">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#CCFF00] mb-4 border border-[#0C0C0C]/10">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-bold">WISH SENT</span>
          </div>
          <h2 className="text-4xl font-black tracking-tight">
            Thank you,<br />
            <span className="text-[#FF4D4D]">{submission.name}!</span>
          </h2>
          <p className="text-muted-foreground mt-2 text-sm">Share it with your people below.</p>
        </motion.div>

        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="rounded-3xl overflow-hidden bg-white border border-border shadow-xl mb-6"
        >
          <img src={imageUrl} alt="Your wish" className="w-full h-auto block" data-testid="submitted-image" />
        </motion.div>

        {/* PRIMARY: Share the actual image file via native share sheet */}
        <Button
          onClick={() => shareImage(null)}
          disabled={sharing}
          data-testid="share-image-btn"
          className="w-full h-14 rounded-full bg-[#FF4D4D] hover:bg-[#FF7373] text-white font-bold text-base shadow-[0_8px_32px_rgba(255,77,77,0.35)] mb-3"
        >
          {sharing ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <Share2 className="h-5 w-5 mr-2" />
              Share photo
            </>
          )}
        </Button>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <button
            onClick={() => shareImage(`https://wa.me/?text=${encodedText}%20${encodedUrl}`)}
            disabled={sharing}
            data-testid="share-whatsapp-btn"
            className="h-14 rounded-full flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold hover:opacity-90 transition disabled:opacity-60"
          >
            <MessageCircle className="h-5 w-5" />
            WhatsApp
          </button>
          <button
            onClick={() => shareImage(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`)}
            disabled={sharing}
            data-testid="share-twitter-btn"
            className="h-14 rounded-full flex items-center justify-center gap-2 bg-black text-white font-bold hover:opacity-90 transition disabled:opacity-60"
          >
            <XIcon />
            Twitter
          </button>
          <button
            onClick={() => shareImage(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`)}
            disabled={sharing}
            data-testid="share-facebook-btn"
            className="h-14 rounded-full flex items-center justify-center gap-2 bg-[#1877F2] text-white font-bold hover:opacity-90 transition disabled:opacity-60"
          >
            <FacebookIcon />
            Facebook
          </button>
          <button
            onClick={() => downloadImage()}
            data-testid="download-btn"
            className="h-14 rounded-full flex items-center justify-center gap-2 bg-white border border-border font-bold hover:bg-muted transition"
          >
            <Download className="h-5 w-5" />
            Download
          </button>
        </div>

        <button
          onClick={onAnother}
          data-testid="send-another-btn"
          className="w-full mt-3 h-12 rounded-full text-sm font-semibold text-muted-foreground hover:text-foreground transition"
        >
          Send another wish →
        </button>
      </div>
    </motion.div>
  );
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
      <path d="M24 12c0-6.627-5.373-12-12-12S0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.47H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.47h-2.796v8.385C19.612 22.954 24 17.99 24 12z" />
    </svg>
  );
}
